import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { Lock, Mail, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/app/reimbursements");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Visual side */}
      <div
        className="hidden lg:block w-[45%] relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgxMTE2NDAwfDA&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0 sw-hero-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3 w-fit">
            <Logo variant="mark" tone="light" className="w-11 h-11 shrink-0" />
            <span className="font-semibold tracking-wide">Welcome Home</span>
          </Link>
          <div>
            <h2 className="sw-heading text-white text-4xl lg:text-5xl leading-[1.05] mb-4">
              Welcome Home,<br />
              <span className="italic font-light text-[#e2e8f0]">your team.</span>
            </h2>
            <p className="text-white/80 text-base max-w-md">{t("app.tagline")}</p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#f6f6f6]">
        <div className="w-full max-w-[420px]">
          <div className="flex justify-end mb-8"><LanguageToggle /></div>
          <h1 className="sw-heading text-[32px] mb-2">{t("login.title")}</h1>
          <p className="text-[#747474] text-[15px] mb-8">{t("login.subtitle")}</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-5">
            <div>
              <label className="sw-label">{t("login.email")}</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="sw-input pl-11"
                  placeholder="name@company.com"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="sw-label">{t("login.password")}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
                <input
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="sw-input pl-11 pr-11"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  aria-label={showPwd ? t("login.password_hide") : t("login.password_show")}
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-[#f0f0f0] text-[#747474] hover:text-[#101010] transition-colors"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-[13px] text-[#881a27] bg-[#e5c1c1] rounded-[10px] px-4 py-2">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="sw-btn-primary justify-center"
            >
              {loading ? t("common.loading") : t("login.submit")}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
